import { Box, Stack, Typography } from '@mui/material'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import { useGetWorkflowState } from '@/service-library/hooks/workflow-states'
import { WorkflowStateAction } from '@/types/workflow-state-actions'

type SplitDocumentDisplayProps = {
  action: WorkflowStateAction
}

export default function SplitDocumentDisplay({
  action,
}: SplitDocumentDisplayProps) {
  const suggestionsOnly = action.params.only_suggest_splits ?? false
  const getThemeColor = useGetThemeColor()

  const { workflowState: splitState } = useGetWorkflowState({
    id: action.params.split_workflow_state_id,
    filters: {
      fields__only: 'id,name,color',
    },
    enabled: !!action.params.split_workflow_state_id && !suggestionsOnly,
  })

  return (
    <Stack direction="column" spacing={0.5}>
      {suggestionsOnly && (
        <>
          <Typography variant="body2" color="warning.main">
            <b>Suggesting Splits Only</b>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            PixyAI will review the document and suggest where to split it, if
            necessary.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If PixyAI suggests that splitting is necessary, the document will
            remain in the current workflow state for manual review.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If PixyAI determines that splitting is not necessary, the document
            will automatically move to the next workflow state.
          </Typography>
        </>
      )}

      {!suggestionsOnly && splitState && (
        <Stack spacing={1} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            PixyAI will review the document and automatically split it into
            multiple new documents if necessary.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After new documents are created by PixyAI based on the original, the
            original document will move to:
          </Typography>
          <Stack spacing={1} direction="row" alignItems="center">
            <Box
              sx={{
                background: getThemeColor(splitState.color),
                width: 4,
                height: 24,
                borderRadius: 4,
              }}
            />
            <Typography variant="body1">{splitState.name}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            If PixyAI determines that splitting is not necessary, the document
            will automatically move to the next workflow state.
          </Typography>
        </Stack>
      )}

      {action.params.additional_instructions && (
        <Stack spacing={0.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <b>Additional Instructions:</b>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {action.params.additional_instructions}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}
